import { articleCritiqueFindingKey } from '@/lib/content-doctrine/article-critique-confirmation';
import { editorialFindingRef } from '@/lib/slack/editorial-note-tag';

/**
 * The read side of the link a Slack tag carries back into Mora:
 * `/reach/editorial?topic=<topicId>&finding=<ref>` (built by `editorialNotePath`). The page
 * reads it once, opens that topic's dock panel, hands the finding ref to the dock, and drops
 * both params from the URL so a refresh or a later topic click does not reopen the note.
 */

export const EDITORIAL_NOTE_LINK_PARAMS = ['topic', 'finding'] as const;

export interface EditorialNoteLink {
  topicId: string;
  /** Absent when the link names only a topic. */
  findingRef: string | null;
}

/** The topic and finding a deep link names, or null when it names no topic. */
export function readEditorialNoteLink(params: URLSearchParams): EditorialNoteLink | null {
  const topicId = params.get('topic')?.trim();
  if (!topicId) return null;
  const findingRef = params.get('finding')?.trim();
  return { topicId, findingRef: findingRef || null };
}

/** A copy of `params` without the note-link params; every other param is kept. */
export function withoutEditorialNoteLink(params: URLSearchParams): URLSearchParams {
  const next = new URLSearchParams(params);
  for (const name of EDITORIAL_NOTE_LINK_PARAMS) next.delete(name);
  return next;
}

/**
 * The full finding key whose short ref matches `findingRef`, or null. The link carries a hash
 * of the key, so the only way back is hashing each finding's key the same way and comparing.
 */
export function findLinkedFindingKey(
  findings: ReadonlyArray<Parameters<typeof articleCritiqueFindingKey>[0]>,
  findingRef: string | null | undefined,
): string | null {
  if (!findingRef) return null;
  for (const finding of findings) {
    const key = articleCritiqueFindingKey(finding);
    if (editorialFindingRef(key) === findingRef) return key;
  }
  return null;
}
